// ═══════════════════════════════════════════════════════════════
// MÓDULO: Gestión de Vacaciones
// Sistema completo de solicitud, aprobación y calendario de vacaciones
// ═══════════════════════════════════════════════════════════════

import React, { useState, useEffect } from 'react';
import {
  Calendar,
  Plus,
  ChevronLeft,
  ChevronRight,
  User,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Trash2
} from 'lucide-react';
import { vacacionesAPI } from '../../api/vacaciones';
import { employeesAPI } from '../../api/employees';
import { useCompany } from '../../contexts/CompanyContext';

export default function VacacionesModule() {
  const { currentCompany } = useCompany();

  // Estados
  const [solicitudes, setSolicitudes] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [calendario, setCalendario] = useState({});
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showCalendar, setShowCalendar] = useState(true);

  // Formulario
  const [formData, setFormData] = useState({
    employee_id: '',
    fecha_inicio_vacaciones: '',
    fecha_fin_vacaciones: '',
    fecha_retorno_labores: '',
    dias_solicitados: 0,
    suplente_id: '',
    instrucciones_suplente: '',
    motivo: ''
  });

  const [employeeBalance, setEmployeeBalance] = useState(null);

  // Calendario
  const [calendarDate, setCalendarDate] = useState(new Date());
  const currentYear = calendarDate.getFullYear();
  const currentMonth = calendarDate.getMonth() + 1;

  // Cargar datos iniciales
  useEffect(() => {
    loadData();
  }, [currentCompany]);

  useEffect(() => {
    if (showCalendar) {
      loadCalendar();
    }
  }, [calendarDate, showCalendar]);

  // Formatea 'YYYY-MM-DD' o ISO string sin desplazamiento de timezone
  const fmtFecha = (d) => {
    if (!d) return '—';
    const s = String(d).split('T')[0]; // queda 'YYYY-MM-DD'
    const [y, m, dd] = s.split('-');
    return `${dd}/${m}/${y}`;
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const [solicitudesData, employeesData, statsData] = await Promise.all([
        vacacionesAPI.getAll(),
        employeesAPI.getAll({ estado: 'activo' }),
        vacacionesAPI.getStats(currentYear)
      ]);

      setSolicitudes(solicitudesData);
      setEmployees(employeesData.employees || []);
      setStats(statsData);
    } catch (error) {
      console.error('Error loading data:', error);
      alert('Error al cargar datos de vacaciones');
    } finally {
      setLoading(false);
    }
  };

  const loadCalendar = async () => {
    try {
      const data = await vacacionesAPI.getCalendario(currentYear, currentMonth);
      setCalendario(data);
    } catch (error) {
      console.error('Error loading calendar:', error);
    }
  };

  // Calcular días solicitados
  useEffect(() => {
    if (formData.fecha_inicio_vacaciones && formData.fecha_fin_vacaciones) {
      const inicio = new Date(formData.fecha_inicio_vacaciones);
      const fin = new Date(formData.fecha_fin_vacaciones);
      const diffTime = Math.abs(fin - inicio);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
      setFormData(prev => ({ ...prev, dias_solicitados: diffDays }));
    }
  }, [formData.fecha_inicio_vacaciones, formData.fecha_fin_vacaciones]);

  // Cargar balance del empleado seleccionado
  useEffect(() => {
    if (formData.employee_id) {
      loadEmployeeBalance(formData.employee_id);
    } else {
      setEmployeeBalance(null);
    }
  }, [formData.employee_id]);

  const loadEmployeeBalance = async (employee_id) => {
    try {
      const balance = await vacacionesAPI.getBalance(employee_id, currentYear);
      setEmployeeBalance(balance);
    } catch (error) {
      console.error('Error loading balance:', error);
      setEmployeeBalance(null);
    }
  };

  // Handlers
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await vacacionesAPI.create(formData);
      alert('Solicitud de vacaciones creada exitosamente');
      setShowForm(false);
      resetForm();
      loadData();
    } catch (error) {
      console.error('Error creating:', error);
      alert(error.response?.data?.error || 'Error al crear solicitud');
    }
  };

  const handleAprobar = async (id) => {
    if (!window.confirm('¿Aprobar esta solicitud de vacaciones?')) return;
    try {
      await vacacionesAPI.aprobar(id);
      alert('Solicitud aprobada correctamente');
      loadData();
      loadCalendar();
    } catch (error) {
      console.error('Error approving:', error);
      alert('Error al aprobar solicitud');
    }
  };

  const handleRechazar = async (id) => {
    const comentarios = prompt('Ingrese el motivo del rechazo:');
    if (!comentarios) return;

    try {
      await vacacionesAPI.rechazar(id, comentarios);
      alert('Solicitud rechazada correctamente');
      loadData();
    } catch (error) {
      console.error('Error rejecting:', error);
      alert('Error al rechazar solicitud');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar/Cancelar esta solicitud?')) return;
    try {
      await vacacionesAPI.delete(id);
      alert('Solicitud eliminada correctamente');
      loadData();
      loadCalendar();
    } catch (error) {
      console.error('Error deleting:', error);
      alert('Error al eliminar solicitud');
    }
  };

  const resetForm = () => {
    setFormData({
      employee_id: '',
      fecha_inicio_vacaciones: '',
      fecha_fin_vacaciones: '',
      fecha_retorno_labores: '',
      dias_solicitados: 0,
      suplente_id: '',
      instrucciones_suplente: '',
      motivo: ''
    });
    setEmployeeBalance(null);
  };

  const changeMonth = (direction) => {
    const newDate = new Date(calendarDate);
    newDate.setMonth(newDate.getMonth() + direction);
    setCalendarDate(newDate);
  };

  // Generar días del mes
  const getDaysInMonth = () => {
    const year = calendarDate.getFullYear();
    const month = calendarDate.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    const days = [];
    // Días vacíos al inicio
    for (let i = 0; i < firstDay; i++) {
      days.push(null);
    }
    // Días del mes
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(i);
    }
    return days;
  };

  const getEmpleadosEnFecha = (day) => {
    if (!day) return [];
    const fecha = `${currentYear}-${String(currentMonth).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return calendario[fecha] || [];
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Gestión de Vacaciones</h1>
          <p className="text-gray-600 mt-1">Control y aprobación de vacaciones</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => setShowCalendar(!showCalendar)}
            className={`px-4 py-2 rounded-lg font-semibold flex items-center gap-2 ${
              showCalendar ? 'bg-purple-600 text-white' : 'bg-gray-200 text-gray-700'
            }`}
          >
            <Calendar className="w-5 h-5" />
            {showCalendar ? 'Ocultar' : 'Ver'} Calendario
          </button>
          <button
            onClick={() => setShowForm(!showForm)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg font-semibold flex items-center gap-2 hover:bg-blue-700"
          >
            <Plus className="w-5 h-5" />
            Nueva Solicitud
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-yellow-50 border-2 border-yellow-200 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-yellow-700 font-semibold">Pendientes</p>
                <p className="text-3xl font-bold text-yellow-900">{stats.pendientes || 0}</p>
              </div>
              <Clock className="w-10 h-10 text-yellow-600" />
            </div>
          </div>

          <div className="bg-green-50 border-2 border-green-200 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-700 font-semibold">Aprobadas</p>
                <p className="text-3xl font-bold text-green-900">{stats.aprobadas || 0}</p>
              </div>
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
          </div>

          <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-red-700 font-semibold">Rechazadas</p>
                <p className="text-3xl font-bold text-red-900">{stats.rechazadas || 0}</p>
              </div>
              <XCircle className="w-10 h-10 text-red-600" />
            </div>
          </div>

          <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-700 font-semibold">Total Días</p>
                <p className="text-3xl font-bold text-blue-900">{stats.total_dias_aprobados || 0}</p>
              </div>
              <Calendar className="w-10 h-10 text-blue-600" />
            </div>
          </div>
        </div>
      )}

      {/* Calendario Visual */}
      {showCalendar && (
        <div className="bg-white rounded-xl shadow-lg border-2 border-gray-200 p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-900">
              Calendario de Vacaciones - {calendarDate.toLocaleDateString('es', { month: 'long', year: 'numeric' })}
            </h2>
            <div className="flex gap-2">
              <button
                onClick={() => changeMonth(-1)}
                className="p-2 bg-gray-100 hover:bg-gray-200 rounded-lg"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                onClick={() => changeMonth(1)}
                className="p-2 bg-gray-100 hover:bg-gray-200 rounded-lg"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div className="grid grid-cols-7 gap-2">
            {/* Cabecera días de la semana */}
            {['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'].map(day => (
              <div key={day} className="text-center font-bold text-gray-700 py-2">
                {day}
              </div>
            ))}

            {/* Días del mes */}
            {getDaysInMonth().map((day, index) => {
              const empleados = getEmpleadosEnFecha(day);
              const isToday = day &&
                day === new Date().getDate() &&
                calendarDate.getMonth() === new Date().getMonth() &&
                calendarDate.getFullYear() === new Date().getFullYear();

              return (
                <div
                  key={index}
                  className={`min-h-24 border-2 rounded-lg p-2 ${
                    day
                      ? isToday
                        ? 'bg-purple-50 border-purple-400'
                        : empleados.length > 0
                        ? 'bg-blue-50 border-blue-200'
                        : 'bg-white border-gray-200'
                      : 'bg-gray-50 border-gray-100'
                  }`}
                >
                  {day && (
                    <>
                      <div className="text-sm font-bold text-gray-700 mb-1">{day}</div>
                      {empleados.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {empleados.slice(0, 3).map((emp, idx) => (
                            <div
                              key={idx}
                              className="group relative"
                              title={`${emp.nombre} ${emp.apellido} - ${emp.cargo}`}
                            >
                              {emp.foto ? (
                                <img
                                  src={emp.foto}
                                  alt={emp.nombre}
                                  className="w-8 h-8 rounded-full border-2 border-white shadow-sm"
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.nextSibling.style.display = 'flex';
                                  }}
                                />
                              ) : null}
                              <div
                                className={`w-8 h-8 rounded-full bg-blue-500 text-white flex items-center justify-center text-xs font-bold ${
                                  emp.foto ? 'hidden' : 'flex'
                                }`}
                              >
                                {emp.nombre[0]}{emp.apellido[0]}
                              </div>
                              {/* Tooltip */}
                              <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10 pointer-events-none">
                                {emp.nombre} {emp.apellido}
                              </div>
                            </div>
                          ))}
                          {empleados.length > 3 && (
                            <div className="w-8 h-8 rounded-full bg-gray-400 text-white flex items-center justify-center text-xs font-bold">
                              +{empleados.length - 3}
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Formulario de Nueva Solicitud */}
      {showForm && (
        <div className="bg-white rounded-xl shadow-lg border-2 border-purple-200 p-6 mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Nueva Solicitud de Vacaciones</h2>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Empleado */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Empleado *
                </label>
                <select
                  value={formData.employee_id}
                  onChange={(e) => setFormData({ ...formData, employee_id: e.target.value })}
                  className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  required
                >
                  <option value="">Seleccionar empleado...</option>
                  {employees.map(emp => (
                    <option key={emp.id} value={emp.id}>
                      {emp.nombre} {emp.apellido} - {emp.cargo}
                    </option>
                  ))}
                </select>

                {/* Balance Info */}
                {employeeBalance && (
                  <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <p className="text-sm font-semibold text-blue-900">Balance de Vacaciones {currentYear}</p>
                    <div className="grid grid-cols-3 gap-2 mt-2 text-xs">
                      <div>
                        <span className="text-gray-600">Asignados:</span>
                        <span className="ml-1 font-bold text-blue-900">{employeeBalance.dias_asignados}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Utilizados:</span>
                        <span className="ml-1 font-bold text-red-600">{employeeBalance.dias_utilizados}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Disponibles:</span>
                        <span className="ml-1 font-bold text-green-600">{employeeBalance.dias_disponibles}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Suplente */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Suplente (opcional)
                </label>
                <select
                  value={formData.suplente_id}
                  onChange={(e) => setFormData({ ...formData, suplente_id: e.target.value })}
                  className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="">Sin suplente</option>
                  {employees.filter(e => e.id !== formData.employee_id).map(emp => (
                    <option key={emp.id} value={emp.id}>
                      {emp.nombre} {emp.apellido} - {emp.cargo}
                    </option>
                  ))}
                </select>
              </div>

              {/* Fecha Inicio */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Fecha de Inicio *
                </label>
                <input
                  type="date"
                  value={formData.fecha_inicio_vacaciones}
                  onChange={(e) => setFormData({ ...formData, fecha_inicio_vacaciones: e.target.value })}
                  className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  required
                />
              </div>

              {/* Fecha Fin */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Fecha de Fin *
                </label>
                <input
                  type="date"
                  value={formData.fecha_fin_vacaciones}
                  onChange={(e) => setFormData({ ...formData, fecha_fin_vacaciones: e.target.value })}
                  min={formData.fecha_inicio_vacaciones}
                  className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  required
                />
              </div>

              {/* Fecha Retorno */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Fecha de Retorno (opcional)
                </label>
                <input
                  type="date"
                  value={formData.fecha_retorno_labores}
                  onChange={(e) => setFormData({ ...formData, fecha_retorno_labores: e.target.value })}
                  min={formData.fecha_fin_vacaciones}
                  className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>

              {/* Días Solicitados */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Días Solicitados
                </label>
                <input
                  type="number"
                  value={formData.dias_solicitados}
                  readOnly
                  className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg bg-gray-50 text-gray-700 font-bold"
                />
              </div>
            </div>

            {/* Instrucciones Suplente */}
            {formData.suplente_id && (
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Instrucciones para el Suplente
                </label>
                <textarea
                  value={formData.instrucciones_suplente}
                  onChange={(e) => setFormData({ ...formData, instrucciones_suplente: e.target.value })}
                  rows="3"
                  className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  placeholder="Tareas o responsabilidades que debe asumir el suplente..."
                />
              </div>
            )}

            {/* Motivo */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Motivo (opcional)
              </label>
              <textarea
                value={formData.motivo}
                onChange={(e) => setFormData({ ...formData, motivo: e.target.value })}
                rows="2"
                className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder="Motivo de las vacaciones..."
              />
            </div>

            {/* Buttons */}
            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={() => { setShowForm(false); resetForm(); }}
                className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg font-semibold hover:bg-gray-300"
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="px-6 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700"
              >
                Crear Solicitud
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Lista de Solicitudes */}
      <div className="bg-white rounded-xl shadow-lg border-2 border-gray-200 p-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Solicitudes de Vacaciones</h2>

        {solicitudes.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <Calendar className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <p className="text-lg font-semibold">No hay solicitudes de vacaciones</p>
            <p className="text-sm">Crea una nueva solicitud para comenzar</p>
          </div>
        ) : (
          <div className="space-y-3">
            {solicitudes.map(solicitud => {
              const empleado = employees.find(e => e.id === solicitud.employee_id);
              const estadoColors = {
                pendiente: 'bg-yellow-50 border-yellow-300 text-yellow-800',
                aprobada: 'bg-green-50 border-green-300 text-green-800',
                rechazada: 'bg-red-50 border-red-300 text-red-800',
                cancelada: 'bg-gray-50 border-gray-300 text-gray-800'
              };

              return (
                <div
                  key={solicitud.id}
                  className={`border-2 rounded-lg p-4 ${estadoColors[solicitud.estado] || 'bg-white border-gray-200'}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4 flex-1">
                      {/* Foto empleado */}
                      {solicitud.empleado_foto ? (
                        <img
                          src={solicitud.empleado_foto}
                          alt={solicitud.empleado_nombre}
                          className="w-16 h-16 rounded-full border-2 border-white shadow-md"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      <div
                        className={`w-16 h-16 rounded-full bg-purple-500 text-white flex items-center justify-center text-xl font-bold ${
                          solicitud.empleado_foto ? 'hidden' : 'flex'
                        }`}
                      >
                        {solicitud.empleado_nombre?.split(' ').map(n => n[0]).join('')}
                      </div>

                      {/* Info */}
                      <div className="flex-1">
                        <h3 className="text-lg font-bold text-gray-900">{solicitud.empleado_nombre}</h3>
                        <p className="text-sm text-gray-600">{solicitud.cargo} - {solicitud.departamento}</p>

                        <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                          <div>
                            <span className="text-gray-600">Desde:</span>
                            <span className="ml-1 font-semibold">{fmtFecha(solicitud.fecha_inicio_vacaciones)}</span>
                          </div>
                          <div>
                            <span className="text-gray-600">Hasta:</span>
                            <span className="ml-1 font-semibold">{fmtFecha(solicitud.fecha_fin_vacaciones)}</span>
                          </div>
                          <div>
                            <span className="text-gray-600">Días:</span>
                            <span className="ml-1 font-bold text-purple-600">{solicitud.dias_solicitados}</span>
                          </div>
                          <div>
                            <span className="text-gray-600">Estado:</span>
                            <span className="ml-1 font-bold uppercase">{solicitud.estado}</span>
                          </div>
                        </div>
                        {solicitud.estado === 'aprobada' && solicitud.aprobado_por_nombre && (
                          <div className="mt-2 text-sm text-green-700">
                            <span className="font-semibold">Aprobado por:</span> {solicitud.aprobado_por_nombre}
                          </div>
                        )}

                        {solicitud.suplente_nombre && (
                          <div className="mt-2 text-sm">
                            <span className="text-gray-600">Suplente:</span>
                            <span className="ml-1 font-semibold">{solicitud.suplente_nombre}</span>
                          </div>
                        )}

                        {solicitud.motivo && (
                          <div className="mt-2 text-sm text-gray-700">
                            <span className="font-semibold">Motivo:</span> {solicitud.motivo}
                          </div>
                        )}

                        {solicitud.comentarios_rechazo && (
                          <div className="mt-2 p-2 bg-red-100 border border-red-300 rounded text-sm text-red-900">
                            <span className="font-semibold">Motivo de rechazo:</span> {solicitud.comentarios_rechazo}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Acciones */}
                    <div className="flex gap-2">
                      {solicitud.estado === 'pendiente' && (
                        <>
                          <button
                            onClick={() => handleAprobar(solicitud.id)}
                            className="p-2 bg-green-500 hover:bg-green-600 text-white rounded-lg"
                            title="Aprobar"
                          >
                            <CheckCircle className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => handleRechazar(solicitud.id)}
                            className="p-2 bg-red-500 hover:bg-red-600 text-white rounded-lg"
                            title="Rechazar"
                          >
                            <XCircle className="w-5 h-5" />
                          </button>
                        </>
                      )}
                      <button
                        onClick={() => handleDelete(solicitud.id)}
                        className="p-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg"
                        title="Eliminar"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
